import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

export const ROBOTICS_VENDOR_VALUES = ['Yaskawa', 'FANUC', 'Yamaha'] as const
export type RoboticsVendor = (typeof ROBOTICS_VENDOR_VALUES)[number]
export const DEFAULT_ROBOTICS_VENDOR: RoboticsVendor = 'Yaskawa'

export interface IngestState {
  structuredInstallPath: string
  selectedDocumentType: string
  siteId: string
  gatewayId: string
  gatewayUrl: string
  webDevProject: string
  plcControllerId: string
  plcVendor: string
  roboticsControllerId: string
  roboticsVendor: RoboticsVendor
  roboticsEquipmentId: string
  /**
   * Ids of ingest jobs this tab began, still awaiting terminal success.
   * Drives the auto-switch onto a finished job's branch — only jobs the
   * user started here may move their active branch. In-memory only: a
   * reload drops the tracking (and the auto-switch) on purpose.
   */
  jobsBegunHere: string[]
  /**
   * For jobs this tab eagerly checked out onto at begin (an ingest from a
   * protected branch mints a fresh ingest branch and we switch the active
   * branch onto it before extraction): the branch that was active *before*
   * the switch, keyed by job id (`null` = the main baseline). Lets a
   * non-success outcome return the user there instead of stranding them on a
   * reaped (cancelled) or half-written (error) branch. In-memory only.
   */
  eagerSwitchedFrom: Record<string, string | null>
  /**
   * GWBK ingest jobs (begun here) that enabled OPC UA tag scraping with
   * credentials — so the proxy will subscribe over a secured/encrypted
   * channel that requires the gateway operator to trust the proxy's OPC UA
   * client certificate. On terminal success the branch-adoption bridge pops
   * a one-time cert-trust notice. In-memory only.
   */
  certTrustPending: Array<{ jobId: string; fileName: string }>
  /**
   * The cert-trust notice currently shown to the user, or null when none is
   * pending dismissal. Set when a tracked job finishes successfully.
   */
  certTrustNotice: { fileName: string } | null
}

function readLocalStorage(key: string, fallback: string): string {
  if (typeof window === 'undefined' || !('localStorage' in window)) return fallback
  return window.localStorage.getItem(key)?.trim() || fallback
}

function writeLocalStorage(key: string, value: string) {
  if (typeof window !== 'undefined' && 'localStorage' in window) {
    window.localStorage.setItem(key, value)
  }
}

function readRoboticsVendor(): RoboticsVendor {
  const savedVendor = readLocalStorage('ontology-robotics-vendor', DEFAULT_ROBOTICS_VENDOR)
  return ROBOTICS_VENDOR_VALUES.includes(savedVendor as RoboticsVendor)
    ? (savedVendor as RoboticsVendor)
    : DEFAULT_ROBOTICS_VENDOR
}

const initialState: IngestState = {
  structuredInstallPath: '',
  selectedDocumentType: 'pid',
  siteId: readLocalStorage('ontology-site-id', 'default-site'),
  gatewayId: readLocalStorage('ontology-gateway-id', 'default-gateway'),
  gatewayUrl: readLocalStorage('ontology-gateway-url', ''),
  webDevProject: readLocalStorage('ontology-web-dev-project', ''),
  plcControllerId: readLocalStorage('ontology-plc-controller-id', ''),
  plcVendor: readLocalStorage('ontology-plc-vendor', ''),
  roboticsControllerId: readLocalStorage('ontology-robotics-controller-id', ''),
  roboticsVendor: readRoboticsVendor(),
  roboticsEquipmentId: readLocalStorage('ontology-robotics-equipment-id', ''),
  jobsBegunHere: [],
  eagerSwitchedFrom: {},
  certTrustPending: [],
  certTrustNotice: null
}

const ingestSlice = createSlice({
  name: 'ingest',
  initialState,
  reducers: {
    setStructuredInstallPath(state, action: PayloadAction<string>) {
      state.structuredInstallPath = action.payload
    },
    setSelectedDocumentType(state, action: PayloadAction<string>) {
      state.selectedDocumentType = action.payload
    },
    setSiteId(state, action: PayloadAction<string>) {
      state.siteId = action.payload
      writeLocalStorage('ontology-site-id', action.payload)
    },
    setGatewayId(state, action: PayloadAction<string>) {
      state.gatewayId = action.payload
      writeLocalStorage('ontology-gateway-id', action.payload)
    },
    setGatewayUrl(state, action: PayloadAction<string>) {
      state.gatewayUrl = action.payload
      writeLocalStorage('ontology-gateway-url', action.payload)
    },
    setWebDevProject(state, action: PayloadAction<string>) {
      state.webDevProject = action.payload
      writeLocalStorage('ontology-web-dev-project', action.payload)
    },
    setPlcControllerId(state, action: PayloadAction<string>) {
      state.plcControllerId = action.payload
      writeLocalStorage('ontology-plc-controller-id', action.payload)
    },
    setPlcVendor(state, action: PayloadAction<string>) {
      state.plcVendor = action.payload
      writeLocalStorage('ontology-plc-vendor', action.payload)
    },
    setRoboticsControllerId(state, action: PayloadAction<string>) {
      state.roboticsControllerId = action.payload
      writeLocalStorage('ontology-robotics-controller-id', action.payload)
    },
    setRoboticsVendor(state, action: PayloadAction<RoboticsVendor>) {
      state.roboticsVendor = action.payload
      writeLocalStorage('ontology-robotics-vendor', action.payload)
    },
    setRoboticsEquipmentId(state, action: PayloadAction<string>) {
      state.roboticsEquipmentId = action.payload
      writeLocalStorage('ontology-robotics-equipment-id', action.payload)
    },
    markJobBegunHere(state, action: PayloadAction<string>) {
      if (!state.jobsBegunHere.includes(action.payload)) {
        state.jobsBegunHere.push(action.payload)
      }
    },
    clearJobBegunHere(state, action: PayloadAction<string>) {
      state.jobsBegunHere = state.jobsBegunHere.filter((id) => id !== action.payload)
    },
    recordJobEagerSwitch(
      state,
      action: PayloadAction<{ jobId: string; fromBranchId: string | null }>
    ) {
      state.eagerSwitchedFrom[action.payload.jobId] = action.payload.fromBranchId
    },
    clearJobEagerSwitch(state, action: PayloadAction<string>) {
      delete state.eagerSwitchedFrom[action.payload]
    },
    markJobNeedsCertTrust(state, action: PayloadAction<{ jobId: string; fileName: string }>) {
      if (!state.certTrustPending.some((e) => e.jobId === action.payload.jobId)) {
        state.certTrustPending.push(action.payload)
      }
    },
    clearJobNeedsCertTrust(state, action: PayloadAction<string>) {
      state.certTrustPending = state.certTrustPending.filter((e) => e.jobId !== action.payload)
    },
    resolveCertTrustForJob(state, action: PayloadAction<string>) {
      const entry = state.certTrustPending.find((e) => e.jobId === action.payload)
      state.certTrustPending = state.certTrustPending.filter((e) => e.jobId !== action.payload)
      if (entry) state.certTrustNotice = { fileName: entry.fileName }
    },
    dismissCertTrustNotice(state) {
      state.certTrustNotice = null
    }
  }
})

export const {
  clearJobBegunHere,
  clearJobEagerSwitch,
  clearJobNeedsCertTrust,
  dismissCertTrustNotice,
  markJobBegunHere,
  markJobNeedsCertTrust,
  recordJobEagerSwitch,
  resolveCertTrustForJob,
  setGatewayId,
  setGatewayUrl,
  setPlcControllerId,
  setPlcVendor,
  setRoboticsControllerId,
  setRoboticsVendor,
  setRoboticsEquipmentId,
  setSelectedDocumentType,
  setSiteId,
  setStructuredInstallPath,
  setWebDevProject
} = ingestSlice.actions

export default ingestSlice.reducer
